import { FC, useMemo, useState } from 'react';

import { searchCouponRecipients } from '@shared/api/giftCoupon/giftCoupon.controller';
import { useCouponRecipientsQuery } from '@shared/api/giftCoupon/giftCoupon.hook';
import { GiftCouponRedeemStatus } from '@shared/api/giftCoupon/giftCoupon.interface';
import { getGiftCsMemoLogAll } from '@shared/api/giftCsMemo/giftCsMemo.controller';
import {
  useGiftCsMemoListQuery,
  useGiftCsMemoLogAllQuery,
  useGiftCsMemoLogCountsQuery,
  useGiftCsMemoLogQuery,
} from '@shared/api/giftCsMemo/giftCsMemo.hook';
import {
  GiftCsMemoLogItem,
  GiftCsMemoLogParams,
  GiftCsMemoLogTargetFilter,
} from '@shared/api/giftCsMemo/giftCsMemo.interface';
import {
  CellDetailPopover,
  CsMemoLogStatsPanel,
  isRoleBadgeRedundant,
} from '@shared/components/csMemo';
import { Loading } from '@shared/components/Loading';
import { toastError, toastSuccess } from '@shared/plugIn/toast';
import { useAuth } from '@shared/state/hooks/useAuth';
import { orderBrandName, shortBrandName } from '@shared/utils/bugoBrandName.util';
import { buildCsMemoStats } from '@shared/utils/csMemoStats';
import { giftDeliveryStatusBadge } from '@shared/utils/giftStatusLabel';

import { CouponRecipientModal, CsMemoModal } from '../components/csMemo';
import {
  redeemStatusColor,
  redeemStatusLabel,
} from '../components/couponDetail/constants';
import { useSessionPersistedState } from '../hooks/useSessionPersistedState';
import { exportCsMemoLogExcel } from '../utils/csMemoLogExcel';
import {
  axisKey,
  couponIdsForDetail,
  deliveryChannelLabel,
  dispatchInfo,
  orderTypeBadge,
  recipientInfo,
  serverMemoCount,
  shareProgress,
  shortOrderNo,
  targetCode,
  targetItemName,
  toCsMemoStatRows,
  withCouponRecipients,
} from '../utils/csMemoLogSummary';

const pad = (n: number) => String(n).padStart(2, '0');
const toDateInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateInput(d);
};

const fmtDateTime = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${String(d.getFullYear()).slice(2)}.${pad(d.getMonth() + 1)}.${pad(
    d.getDate(),
  )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtPhone = (phone: string | undefined) => {
  if (!phone) return '';
  const n = phone.replace(/[^0-9]/g, '');
  return n.length === 11 ? `${n.slice(0, 3)}-${n.slice(3, 7)}-${n.slice(7)}` : phone;
};

//* 이 이상이면 요약 칩을 전량 수집하지 않는다 (200건당 1요청 — 3요청까지만 허용)
const FULL_SUMMARY_LIMIT = 600;

const TARGET_FILTERS: Array<{ value: GiftCsMemoLogTargetFilter; label: string }> = [
  { value: 'all', label: '전체' },
  { value: 'order', label: '주문' },
  { value: 'coupon', label: '쿠폰' },
  { value: 'delivery', label: '배송' },
];

const roleBadge = (item: GiftCsMemoLogItem) =>
  item.byRole === 'Admin'
    ? { label: '관리자', className: 'bg-indigo-100 text-indigo-700' }
    : { label: '마리', className: 'bg-teal-100 text-teal-700' };

const unitBadge = (item: GiftCsMemoLogItem) => {
  if (item.giftCoupon) return { label: '쿠폰', className: 'bg-blue-100 text-blue-700' };
  if (item.giftDelivery) return { label: '배송', className: 'bg-teal-100 text-teal-700' };
  return { label: '주문', className: 'bg-gray-100 text-gray-600' };
};

/** 서버가 준 교환상태 문자열 → 화면 라벨·색 (모르는 값이면 원문 그대로) */
const redeemBadge = (status: string | undefined) => {
  if (!status) return null;
  const key = status as GiftCouponRedeemStatus;
  return {
    label: redeemStatusLabel[key] ?? status,
    className: redeemStatusColor[key] ?? 'bg-gray-100 text-gray-600',
  };
};

/**
 * 상태 배지 — 쿠폰 축은 **발송(교환)** 상태, 배송 축은 **배송** 상태. 역할이 같아 한 열에 담는다.
 *
 * ⚠️ 두 축의 라벨이 겹쳐서(`발송완료` vs `배송완료`) 배지만 보면 무슨 상태인지 추론해야 했다.
 * 그래서 축 이름(`axis`)을 같이 돌려주고 화면·통계·엑셀이 전부 **축을 앞에 붙여** 쓴다.
 */
const dispatchBadge = (item: GiftCsMemoLogItem) => {
  if (item.coupon) {
    const badge = redeemBadge(item.coupon.redeemStatus);
    return badge ? { ...badge, axis: '발송' } : null;
  }
  if (item.delivery?.deliveryStatus) {
    return { ...giftDeliveryStatusBadge(item.delivery.deliveryStatus), axis: '배송' };
  }
  return null;
};

//* 통계·칩·엑셀이 쓰는 상태 문자열 — 축을 붙여야 한 분포 안에서 두 축이 섞이지 않는다
const dispatchStatusText = (item: GiftCsMemoLogItem): string => {
  const badge = dispatchBadge(item);
  return badge ? `${badge.axis} ${badge.label}` : '';
};

const fmtDate = (date: Date | string | undefined) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${String(d.getFullYear()).slice(2)}.${pad(d.getMonth() + 1)}.${pad(
    d.getDate(),
  )}`;
};

//* 응대 이력 모달 헤더 — 응대는 여기서 일어나므로 표보다 두텁게 요약한다.
//* 송장번호·택배사는 표에 올리면 대상 열이 다시 뚱뚱해지므로 여기서만 보여준다.
const modalSubtitle = (item: GiftCsMemoLogItem) => {
  const dispatched = dispatchInfo(item);
  const shared = shareProgress(item);
  //* 수령자는 주문자와 다른 사람 — 쿠폰은 교환신청자, 배송은 건별 수령자
  const recipient = recipientInfo(item);
  const line = [
    item.order && `주문 #${shortOrderNo(item)}`,
    item.order?.receiverName && `주문자 ${item.order.receiverName}`,
    item.order?.receiverPhoneNumber && fmtPhone(item.order.receiverPhoneNumber),
    recipient && `수령 ${recipient.name}`,
    recipient?.phoneNumber && fmtPhone(recipient.phoneNumber),
    recipient?.at && `${recipient.at.label} ${fmtDate(recipient.at.date)}`,
    orderTypeBadge(item)?.label,
    targetItemName(item),
    dispatchStatusText(item),
    dispatched && `${dispatched.label} ${fmtDate(dispatched.date)}`,
    deliveryChannelLabel(item.coupon?.deliveryChannel),
    //* "보냈는데 못 받았대요"의 답은 발송이 아니라 공유·열람 쪽에 있다
    shared && (shared.date ? `${shared.label} ${fmtDate(shared.date)}` : shared.label),
    !!item.coupon?.undeliverCount && `발송취소 ${item.coupon.undeliverCount}회`,
    item.order?.purchasedAt && `결제 ${fmtDate(item.order.purchasedAt)}`,
    item.delivery?.carrier,
    item.delivery?.trackingNumber,
  ]
    .filter(Boolean)
    .join(' · ');
  return line || undefined;
};

/** 응대 이력을 열 축 — 셋 다 없는 레거시 메모는 열 수 없다 */
const memoTargetOf = (item: GiftCsMemoLogItem) => {
  if (item.giftCoupon) return { giftCoupon: item.giftCoupon };
  if (item.giftDelivery) return { giftDelivery: item.giftDelivery };
  if (item.giftOrder) return { giftOrder: item.giftOrder };
  return null;
};

/**
 * CS 응대 로그 — 남긴 메모를 기간별로 쭉 훑어보는 표.
 * 어떤 문의가 얼마나 들어왔는지 파악하고 응대 매뉴얼을 만드는 용도라 엑셀로도 뽑는다.
 * Admin 전용 메모는 서버가 Admin에게만 내려준다(마리 계정은 목록에서 아예 제외).
 */
export const GiftCsMemoLogPage: FC = function GiftCsMemoLogPage() {
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [from, setFrom] = useSessionPersistedState('giftCsMemoLog.from', daysAgo(30));
  const [to, setTo] = useSessionPersistedState(
    'giftCsMemoLog.to',
    toDateInput(new Date()),
  );
  const [target, setTarget] = useSessionPersistedState<GiftCsMemoLogTargetFilter>(
    'giftCsMemoLog.target',
    'all',
  );
  const [adminOnly, setAdminOnly] = useSessionPersistedState(
    'giftCsMemoLog.adminOnly',
    false,
  );
  //* 기본 20 — 50건은 한 화면에서 훑기엔 스크롤이 너무 길다 (개수는 아래 셀렉트로 조절)
  const [limit, setLimit] = useSessionPersistedState('giftCsMemoLog.limit', 20);
  // 입력 중인 검색어와 실제 조회에 쓰는 검색어를 분리 (타이핑마다 조회하지 않는다)
  const [searchInput, setSearchInput] = useSessionPersistedState('giftCsMemoLog.q', '');
  const [query, setQuery] = useSessionPersistedState('giftCsMemoLog.appliedQ', '');
  const [page, setPage] = useState(1);
  const [modalItem, setModalItem] = useState<GiftCsMemoLogItem | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  //* 인증·서명은 표에 올리면 열이 터진다 — 수령자 칸을 눌러 모달로 본다
  const [recipientItem, setRecipientItem] = useState<GiftCsMemoLogItem | null>(null);
  //* 통계는 조회 조건 전체를 다시 긁어와야 해서 **열었을 때만** 돈다
  const [showStats, setShowStats] = useSessionPersistedState(
    'giftCsMemoLog.showStats',
    false,
  );

  const params = useMemo<GiftCsMemoLogParams>(
    () => ({
      from,
      to,
      target,
      ...(query ? { q: query } : {}),
      ...(admin && adminOnly ? { adminOnly: true } : {}),
      page,
      limit,
    }),
    [from, to, target, query, admin, adminOnly, page, limit],
  );

  const { data, isLoading, isFetching } = useGiftCsMemoLogQuery(params);
  //* 필터 칩 숫자 — 어느 축을 볼지 **누르기 전에** 알 수 있어야 한다 (합계 = 총 N건)
  const { data: unitCounts } = useGiftCsMemoLogCountsQuery(params);
  const unavailable = data === null;
  const rawItems = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  //* 쿠폰 수령자(교환신청자) 임시 보강 — 백엔드 REQ-6 구현 완료·**배포 대기** 구간용.
  //* 배포되면 coupon.recipientName 이 실려 대상이 0건이 되고 요청 자체가 사라진다.
  //* (스펙: docs/shop-order-cs-memo-log-enrich-spec.md §10~§12)
  const detailCouponIds = useMemo(() => couponIdsForDetail(rawItems), [rawItems]);
  const { data: couponRecipients } = useCouponRecipientsQuery(detailCouponIds);
  const items = useMemo(
    () => withCouponRecipients(rawItems, couponRecipients),
    [rawItems, couponRecipients],
  );

  //* 요약 칩·통계는 조회 조건 **전체**를 모아서 센다. 표 한 페이지만 세면
  //* "총 42건"인데 칩 합계는 20이 되어 두 숫자가 서로를 부정한다.
  //* 다만 전량 수집은 200건당 1요청이라, 통계 패널을 열었거나 전체가 이 정도 규모일 때만 돈다.
  const wantFullSummary = !unavailable && total > 0 && total <= FULL_SUMMARY_LIMIT;
  const { data: allData, isLoading: isStatsLoading } = useGiftCsMemoLogAllQuery(
    params,
    showStats || wantFullSummary,
  );
  //* 전체를 못 받은 구간(너무 많음)에서는 페이지 기준으로 근사하고 라벨로 그렇게 밝힌다
  const summaryItems = allData?.items ?? items;
  const summaryScope = allData ? '' : '이 페이지 ';

  //* 값이 하나도 없는 열은 띄우지 않는다 — '-'만 늘어선 죽은 열이 표를 넓히기만 한다.
  //* (서버가 내려주기 시작하면 프론트 배포 없이 열이 살아난다)
  const columns = useMemo(
    () => ({
      brand: items.some((item) => orderBrandName(item.order)),
      recipient: items.some((item) => !!recipientInfo(item) || !!item.coupon?.memo),
    }),
    [items],
  );

  // 응대 건수는 **축 기준**으로 센다 — 쿠폰 행은 그 쿠폰, 배송 행은 그 배송, 주문 행은 그 주문 축.
  // `이력 보기`를 눌렀을 때 실제로 보이는 범위와 같아야 하기 때문이다.
  // 서버가 축별 memoCount를 주면 그걸 쓰고, 아직 안 오면 이 페이지에서 세어 근사한다.
  const pageMemoCountByAxis = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of items) {
      const key = axisKey(item);
      if (key) map[key] = (map[key] ?? 0) + 1;
    }
    return map;
  }, [items]);

  // 분포는 조회 결과 전체 기준(못 받았으면 페이지 기준 — 라벨이 그걸 밝힌다)
  const writerCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of summaryItems) {
      map.set(item.byName, (map.get(item.byName) ?? 0) + 1);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [summaryItems]);

  // 유형(쿠폰형·배송형·혼합)은 주문 속성이라 서버 집계가 없다 → 여기서 직접 센다
  const orderTypeCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of summaryItems) {
      const label = orderTypeBadge(item)?.label;
      if (label) map.set(label, (map.get(label) ?? 0) + 1);
    }
    return ['쿠폰형', '배송형', '혼합']
      .map((label) => [label, map.get(label) ?? 0] as const)
      .filter(([, count]) => count > 0);
  }, [summaryItems]);

  // "어떤 상태의 건에서 문의가 나오나"를 표 훑기 전에 보여준다 (축 라벨 포함 문자열)
  const statusCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of summaryItems) {
      const label = dispatchStatusText(item);
      if (label) map.set(label, (map.get(label) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [summaryItems]);

  // 상조 분포 — "웅진프리드라이프 건이 몇 건인지"를 표 훑기 전에
  const brandCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of summaryItems) {
      const name = orderBrandName(item.order);
      if (name) map.set(name, (map.get(name) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [summaryItems]);

  const stats = useMemo(
    () =>
      allData
        ? buildCsMemoStats(
            toCsMemoStatRows(allData.items, dispatchStatusText),
            allData.total,
          )
        : null,
    [allData],
  );

  const applyRange = (fromDate: string) => {
    setFrom(fromDate);
    setTo(toDateInput(new Date()));
    setPage(1);
  };

  const runSearch = () => {
    setQuery(searchInput.trim());
    setPage(1);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      //* 통계 패널과 같은 수집 함수 — 두 화면이 다른 모집단을 보지 않게
      const { items: all, capped } = await getGiftCsMemoLogAll(params);
      if (all.length === 0) {
        toastError('내보낼 메모가 없습니다.');
        return;
      }
      //* 화면과 같은 기준으로 쿠폰 수령자를 채워 내보낸다 (서버 요약에 실리면 대상 0건)
      const enriched = withCouponRecipients(
        all,
        await searchCouponRecipients(couponIdsForDetail(all)).catch(() => ({})),
      );
      exportCsMemoLogExcel(enriched, { from, to });
      if (capped) {
        toastSuccess(
          `${all.length}건 내보냈습니다. (한 번에 최대 2,000건 — 기간을 나눠 받아주세요)`,
        );
      }
    } catch {
      toastError('엑셀 내보내기에 실패했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  const modalTarget = modalItem ? memoTargetOf(modalItem) : null;

  // 쿠폰·배송 축은 서버가 그 건만 돌려주므로 모달이 알아서 조회하면 된다.
  // 주문 축만 다르다 — `?giftOrder=` 는 그 주문의 쿠폰·배송 메모까지 전부 주는 통합 타임라인이다.
  // 이 화면은 행마다 "그 단위의 응대"를 보여주는 규칙이라, 주문 축은 주문 축 메모만 남기고 거른다.
  // (쿠폰·배송 건 응대는 각자의 행에서 보면 된다)
  const orderAxisId =
    modalTarget && !modalItem?.giftCoupon && !modalItem?.giftDelivery
      ? modalItem?.giftOrder
      : undefined;
  const orderAxisQuery = useGiftCsMemoListQuery(
    orderAxisId ? { giftOrder: orderAxisId } : {},
    !!orderAxisId,
  );
  const orderAxisMemos = useMemo(
    () => (orderAxisQuery.data ?? []).filter((m) => !m.giftCoupon && !m.giftDelivery),
    [orderAxisQuery.data],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold">CS 응대 로그</h1>
            <p className="mt-1 text-sm text-gray-500">
              주문·쿠폰·배송에 남긴 응대 메모 전체 · 기간별 조회
              {admin ? ' · 관리자 전용 메모 포함' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* 표를 줄글로 훑기 전에 "어디에 몰리나"부터 보는 입구 */}
            <button
              type="button"
              onClick={() => setShowStats(!showStats)}
              disabled={unavailable}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
                showStats
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'
              }`}
              title="조회 조건 전체(최대 2,000건)를 집계해 문의 유형·시간대·상품 분포를 보여줍니다"
            >
              {showStats ? '통계 닫기' : '통계 보기'}
            </button>
            <button
              type="button"
              onClick={() => void handleExport()}
              disabled={isExporting || unavailable || total === 0}
              className="rounded-lg border border-emerald-300 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              title="현재 조회 조건 그대로 엑셀로 내보내기 (응대 매뉴얼용)"
            >
              {isExporting ? '내보내는 중...' : '엑셀 내보내기'}
            </button>
          </div>
        </div>
      </div>

      {/* 필터 */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <span className="text-gray-400">~</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border px-3 py-2 text-sm"
          />
          <div className="flex items-center gap-1">
            {[
              { label: '오늘', days: 0 },
              { label: '7일', days: 7 },
              { label: '30일', days: 30 },
              { label: '90일', days: 90 },
            ].map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => applyRange(daysAgo(preset.days))}
                className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
              >
                {preset.label}
              </button>
            ))}
          </div>

          <span className="mx-1 h-5 w-px bg-gray-200" />

          <div className="flex items-center gap-1">
            {TARGET_FILTERS.map((option) => {
              //* null = 엔드포인트 미배포 → 숫자 없이 라벨만 (구서버 안전장치)
              const count = unitCounts?.[option.value];
              const selected = target === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setTarget(option.value);
                    setPage(1);
                  }}
                  className={`rounded-full px-3 py-1 text-xs font-medium ${
                    selected
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                  {count !== undefined && (
                    <span
                      className={`ml-1 font-semibold ${
                        selected
                          ? 'text-indigo-100'
                          : count === 0
                          ? 'text-gray-300'
                          : 'text-indigo-600'
                      }`}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {admin && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={adminOnly}
                onChange={(e) => {
                  setAdminOnly(e.target.checked);
                  setPage(1);
                }}
                className="h-3.5 w-3.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              관리자 전용만
            </label>
          )}

          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runSearch();
            }}
            placeholder="내용, 작성자, 쿠폰번호, 주문자"
            className="min-w-[220px] flex-1 rounded-lg border px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={runSearch}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            검색
          </button>
          {query && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setQuery('');
                setPage(1);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              초기화
            </button>
          )}
        </div>

        {/* 요약 */}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>
            총 <span className="font-bold text-gray-800">{total}</span>건
          </span>
          {isFetching && <span className="text-indigo-500">불러오는 중...</span>}
          {writerCounts.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">{summaryScope}작성자</span>
              {writerCounts.map(([name, count]) => (
                <span
                  key={name}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {name} {count}
                </span>
              ))}
            </>
          )}
          {orderTypeCounts.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">{summaryScope}유형</span>
              {orderTypeCounts.map(([label, count]) => (
                <span
                  key={label}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {label} {count}
                </span>
              ))}
            </>
          )}
          {statusCounts.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">{summaryScope}상태</span>
              {statusCounts.map(([label, count]) => (
                <span
                  key={label}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {label} {count}
                </span>
              ))}
            </>
          )}
          {brandCounts.length > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="text-gray-400">{summaryScope}상조</span>
              {brandCounts.map(([name, count]) => (
                <span
                  key={name}
                  title={name}
                  className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-600"
                >
                  {shortBrandName(name)} {count}
                </span>
              ))}
            </>
          )}
        </div>
      </div>

      {/* 통계 — 표 위에 둔다. 훑기 전에 결론부터 보고 필요한 축으로 좁히는 순서 */}
      {showStats && !unavailable && (
        <div className="px-4 pt-4">
          <CsMemoLogStatsPanel
            stats={stats}
            isLoading={isStatsLoading}
            capped={allData?.capped ?? false}
            statusTitle="건의 상태"
            onPickQuery={(q) => {
              setSearchInput(q);
              setQuery(q);
              setPage(1);
            }}
          />
        </div>
      )}

      {/* 목록 */}
      <div className="p-4">
        {isLoading ? (
          <div className="center-box py-20">
            <Loading />
          </div>
        ) : unavailable ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
            CS 응대 로그가 아직 서버에 반영되지 않았습니다.
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white py-16 text-center text-sm text-gray-400">
            조회 기간에 남긴 메모가 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-white">
            {/* min-w를 못 박아야 좁은 화면에서 열을 짜부라뜨리지 않고 가로 스크롤로 넘어간다.
                min-w-full만 두면 표가 컨테이너에 억지로 맞춰지며 글자가 세로로 쪼개진다 */}
            <table className="w-full min-w-[1250px] text-sm">
              {/* 열 순서 = 로그를 읽는 순서.
                  "언제 · 어떤 건(구분·상태) · 무슨 응대(내용)" 가 왼쪽에 모여야
                  쭉 훑으면서 문의 패턴이 잡힌다. 식별자(대상코드)·사람은 그다음,
                  확인이 필요할 때만 보는 값이라 오른쪽으로 뺐다. */}
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  {/* 순번 — 페이지를 넘겨도 이어진다(2페이지 첫 줄이 21번). "몇 번 건" 으로
                      서로 가리킬 수 있어야 응대 회의·매뉴얼 작성이 된다 */}
                  <th className="whitespace-nowrap px-3 py-3 font-medium">순번</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">작성일시</th>
                  {/* 단위 = 메모가 어느 축에 달렸나(메모 속성) / 유형 = 주문이 무슨 상품인가(주문 속성).
                      배지 둘 다 한 칸에 — 따로 읽을 일이 없다 */}
                  <th className="whitespace-nowrap px-4 py-3 font-medium">구분</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">상태</th>
                  {/* 이 표의 본문. 매뉴얼 재료라 가장 넓고 가장 왼쪽에 가깝게 */}
                  <th className="px-4 py-3 font-medium">응대 내용</th>
                  <th className="whitespace-nowrap px-4 py-3 font-medium">대상 · 상품</th>
                  {/* 상조 — 응대의 첫 분기(상조마다 정산·발송 정책이 갈린다)라 주문자와 열을 나눈다 */}
                  {columns.brand && (
                    <th className="whitespace-nowrap px-4 py-3 font-medium">상조</th>
                  )}
                  <th className="whitespace-nowrap px-4 py-3 font-medium">주문자</th>
                  {/* 실제 받은 사람은 주문자(상주)와 다른 사람이라 열을 따로 둔다 —
                      쿠폰은 교환신청자, 배송은 건별 수령자 */}
                  {columns.recipient && (
                    <th className="whitespace-nowrap px-4 py-3 font-medium">수령자</th>
                  )}
                  {/* '응대 내용' 열은 이 메모 한 건, 이 열은 그 건에 쌓인 응대 흐름 전체로 가는 입구 */}
                  <th className="whitespace-nowrap px-4 py-3 font-medium">이력</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, index) => {
                  //* 최신순 목록이라 **가장 최근이 가장 큰 번호**(= 총 건수)이고 아래로 갈수록 줄어든다.
                  //* 새 메모가 쌓여도 과거 건의 번호가 안 밀리도록 총 건수에서 역산한다
                  const rowNo = total - ((page - 1) * limit + index);
                  const badge = unitBadge(item);
                  const role = roleBadge(item);
                  const orderType = orderTypeBadge(item);
                  const statusBadge = dispatchBadge(item);
                  const dispatched = dispatchInfo(item);
                  const channel = deliveryChannelLabel(item.coupon?.deliveryChannel);
                  const shared = shareProgress(item);
                  const recipient = recipientInfo(item);
                  //* 구매자가 남긴 호칭 메모 — 쿠폰은 이름 없이 신청되는 건이 많아 이게 이름 자리다
                  const couponMemo = item.coupon?.memo;
                  const brandName = orderBrandName(item.order);
                  //* 서버 축별 memoCount가 있으면 그게 정답(전체 기준), 없으면 페이지 기준 근사
                  const totalCount = serverMemoCount(item);
                  const key = axisKey(item);
                  const memoCount =
                    totalCount ?? (key ? pageMemoCountByAxis[key] ?? 1 : 1);
                  return (
                    <tr
                      key={item._id}
                      className={item.adminOnly ? 'bg-amber-50/50' : 'hover:bg-gray-50'}
                    >
                      <td className="whitespace-nowrap px-3 py-2.5 align-top text-xs tabular-nums text-gray-400">
                        {rowNo}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 align-top text-xs text-gray-500">
                        {fmtDateTime(item.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 align-top">
                        <span
                          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                        {orderType && (
                          <span
                            className={`ml-1 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${orderType.className}`}
                          >
                            {orderType.label}
                          </span>
                        )}
                      </td>
                      {/* 상태 — 축(발송/배송)을 배지 앞에 붙여 무슨 상태인지 읽는 순간 알게 한다.
                          매체·공유 진행·발송취소는 셀 폭만 늘리므로 클릭 툴팁으로 접었다 */}
                      <td className="whitespace-nowrap px-4 py-2.5 align-top text-xs">
                        {statusBadge ? (
                          <>
                            <span className="mr-1 text-[10px] text-gray-400">
                              {statusBadge.axis}
                            </span>
                            <span
                              className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${statusBadge.className}`}
                            >
                              {statusBadge.label}
                            </span>
                            <CellDetailPopover
                              title={`${statusBadge.axis} 상세`}
                              lines={[
                                dispatched && {
                                  label: dispatched.label,
                                  text: fmtDate(dispatched.date),
                                },
                                //* 매체마다 발신번호·알림 종류가 달라 "모르는 번호로 문자 왔다" 응대용
                                channel ? { label: '매체', text: channel } : null,
                                //* "보냈는데 못 받았다"의 답은 발송이 아니라 공유·열람 쪽에 있다
                                shared && {
                                  label: '공유',
                                  text: shared.date
                                    ? `${shared.label} ${fmtDate(shared.date)}`
                                    : shared.label,
                                },
                                //* 발송했다 취소된 이력 — sentAt은 지워져 이 값으로만 알 수 있다
                                !!item.coupon?.undeliverCount && {
                                  label: '발송취소',
                                  text: `${item.coupon.undeliverCount}회 (재발송 시 발송일이 새로 박힘)`,
                                  tone: 'warn' as const,
                                },
                              ]}
                            />
                            {dispatched && (
                              <div className="mt-0.5 text-[11px] text-gray-400">
                                {dispatched.label} {fmtDate(dispatched.date)}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      {/* 유일하게 늘어나는 열 — min-w가 없으면 다른 열에 밀려 폭 0까지 눌린다.
                          작성자는 본문에 붙여 읽는 게 자연스러워 열을 따로 두지 않는다 */}
                      <td className="min-w-[300px] max-w-[560px] px-4 py-2.5 align-top">
                        <p className="whitespace-pre-wrap break-words text-xs leading-relaxed text-gray-800">
                          {/* 공개범위는 작성자가 아니라 이 메모 자체의 속성이라 본문 옆에 둔다.
                              행 배경(amber)이 뭘 뜻하는지도 여기서 설명된다 */}
                          {item.adminOnly && (
                            <span className="mr-1 inline-flex rounded bg-amber-200 px-1.5 py-0.5 align-middle text-[10px] font-semibold leading-tight text-amber-800">
                              관리자 전용
                            </span>
                          )}
                          {item.body}
                        </p>
                        <div className="mt-1 text-[11px] text-gray-400">
                          {item.byName}
                          {/* 이름이 이미 역할을 말하고 있으면(Admin 계정명 "관리자") 배지는 중복이라 뺀다 */}
                          {!isRoleBadgeRedundant(item.byName, role.label) && (
                            <span
                              className={`ml-1 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${role.className}`}
                            >
                              {role.label}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* 대상 코드와 상품은 "어떤 건인지" 하나를 이루는 값이라 한 칸에.
                          break-keep — 상품명이 좁아졌을 때 글자 단위로 끊기지 않게 */}
                      <td className="min-w-[160px] px-4 py-2.5 align-top text-xs">
                        <div className="font-mono text-gray-700">
                          {targetCode(item) || <span className="text-gray-300">-</span>}
                        </div>
                        {targetItemName(item) && (
                          <div className="break-keep text-gray-500">
                            {targetItemName(item)}
                          </div>
                        )}
                      </td>
                      {/* 상조 — "웅진프리드라이프 건인지"가 응대의 첫 분기.
                          긴 이름은 닉네임/축약, 전체명은 title로 */}
                      {columns.brand && (
                        <td className="whitespace-nowrap px-4 py-2.5 align-top text-xs">
                          {brandName ? (
                            <span
                              className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600"
                              title={brandName}
                            >
                              {shortBrandName(brandName)}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-2.5 align-top text-xs">
                        {item.order ? (
                          <>
                            <div className="text-gray-700">
                              {item.order.receiverName ?? '-'}
                            </div>
                            {item.order.receiverPhoneNumber && (
                              <div className="text-gray-400">
                                {fmtPhone(item.order.receiverPhoneNumber)}
                              </div>
                            )}
                            {/* 주문번호·결제일은 확인용이라 한 줄로 눌러 담는다 */}
                            <div className="text-[11px] text-gray-400">
                              <span title="주문번호 (주문 ID 뒤 6자리)">
                                #{shortOrderNo(item)}
                              </span>
                              {item.order.purchasedAt &&
                                ` · 결제 ${fmtDate(item.order.purchasedAt)}`}
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      {/* 수령자 — 쿠폰은 교환신청자(구매자가 공유 → 받은 사람이 직접 신청),
                          배송은 건별 수령자. 아직 신청 전이면 상태 열의 공유 진행이 답이다 */}
                      {columns.recipient && (
                        <td className="whitespace-nowrap px-4 py-2.5 align-top text-xs">
                          {recipient || couponMemo ? (
                            //* 쿠폰은 이름 없이 신청되는 건이 많다 — 구매자가 남긴 메모("김부장님")가
                            //* 사실상 이름 자리다. 인증·서명은 눌러서 모달로.
                            <button
                              type="button"
                              onClick={() => item.coupon && setRecipientItem(item)}
                              disabled={!item.coupon}
                              className="rounded text-left hover:bg-indigo-50 disabled:hover:bg-transparent"
                              title={
                                item.coupon
                                  ? '수령자 정보 · 번호 인증 · 서명 보기'
                                  : undefined
                              }
                            >
                              {/* 메모가 첫 줄 — 표에서 바로 읽혀야 하는 값이라 모달에 숨기지 않는다 */}
                              {couponMemo && (
                                <div className="font-medium text-gray-800 underline decoration-dotted">
                                  {couponMemo}
                                </div>
                              )}
                              {!couponMemo && recipient?.name && (
                                <div className="text-gray-700 underline decoration-dotted">
                                  {recipient.name}
                                </div>
                              )}
                              {!couponMemo && !recipient?.name && (
                                <div className="text-[11px] text-gray-300 underline decoration-dotted">
                                  이름·메모 없음
                                </div>
                              )}
                              {/* 메모가 이름 자리를 대신했으면 실제 신청자 이름은 아래로 */}
                              {couponMemo && recipient?.name && (
                                <div className="text-[11px] text-gray-400">
                                  신청 {recipient.name}
                                </div>
                              )}
                              {recipient?.phoneNumber && (
                                <div className="text-gray-400">
                                  {fmtPhone(recipient.phoneNumber)}
                                </div>
                              )}
                              {recipient?.at && (
                                <div
                                  className="text-[11px] text-gray-400"
                                  title={
                                    recipient.at.label === '신청'
                                      ? '수령자가 교환신청한 날 (구매자 공유일·관리자 발송일과 다름)'
                                      : '수령자가 배송지를 제출한 날 (일괄배송은 값이 없습니다)'
                                  }
                                >
                                  {recipient.at.label} {fmtDate(recipient.at.date)}
                                </div>
                              )}
                            </button>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-4 py-2.5 align-top">
                        {memoTargetOf(item) ? (
                          <div className="flex items-center gap-1.5">
                            {/* 건수를 버튼 안에 넣으면 행마다 버튼 폭이 달라져 어색하다 —
                                버튼은 고정 크기로 두고 건수는 옆에 따로 */}
                            <button
                              type="button"
                              onClick={() => setModalItem(item)}
                              className="rounded-md border border-indigo-200 px-2 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-50"
                              title={`이 ${badge.label} 건의 응대 이력 전체 보기 · 이어서 남기기`}
                            >
                              이력 보기
                            </button>
                            {/* 2건 이상일 때만 — 1건은 굳이 알릴 필요가 없다 */}
                            {memoCount > 1 && (
                              <span
                                className="whitespace-nowrap text-[11px] font-semibold text-indigo-700"
                                title={
                                  totalCount === undefined
                                    ? `이 ${badge.label} 건에 쌓인 응대 건수 (지금 보고 있는 페이지 기준)`
                                    : `이 ${badge.label} 건의 전체 응대 건수`
                                }
                              >
                                {memoCount}건
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[11px] text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 페이지 이동 */}
        {!unavailable && total > 0 && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>페이지당</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-lg border px-2 py-1 text-xs"
              >
                {[20, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}건
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                이전
              </button>
              <span className="text-xs text-gray-500">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </div>

      {recipientItem && (
        <CouponRecipientModal
          item={recipientItem}
          onClose={() => setRecipientItem(null)}
        />
      )}

      {modalItem && modalTarget && (
        <CsMemoModal
          title={
            modalItem.giftCoupon
              ? `쿠폰 ${targetCode(modalItem)} 응대 이력`
              : modalItem.giftDelivery
              ? `배송 ${modalItem.delivery?.code ?? ''} 응대 이력`
              : '주문 응대 이력'
          }
          subtitle={modalSubtitle(modalItem)}
          target={modalTarget}
          {...(orderAxisId
            ? {
                memos: orderAxisMemos,
                isLoading: orderAxisQuery.isLoading,
                unavailable: orderAxisQuery.data === null,
                notice:
                  '이 주문 단위에 남긴 응대만 보여줍니다. 쿠폰·배송 건의 응대는 각 행의 [이력 보기]에서 확인하세요.',
              }
            : {})}
          onClose={() => setModalItem(null)}
          zIndexClassName="z-50"
        />
      )}
    </div>
  );
};
